import { Alert, Code, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { ApiError, ApiUnreachableError } from '../api/client.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import type { Vars } from '../i18n/core.js';

/**
 * How a failure is shown.
 *
 * One component, because the API's refusals are the useful part of this system
 * and each page inventing its own presentation is how they turn into
 * "Something went wrong". A refusal here already says what is wrong and often
 * what to do; the job is to not lose that on the way to the screen.
 */

export interface ErrorMessageProps {
  readonly error: unknown;
  readonly title?: string;
}

type Translate = (key: MessageKey, vars?: Vars) => string;

/**
 * The text to show for anything that can be thrown.
 *
 * Takes `t` rather than calling the hook, because it is used outside components
 * too — from a mutation's `onError`, where there is no render in progress.
 *
 * **The API's own messages are not translated**, and cannot be: they arrive
 * from the server in one language. Only the one message this layer writes
 * itself — "the server could not be reached", which is the case where no server
 * message exists — has a key. That asymmetry is real and worth seeing: a
 * refusal from the API reads in Danish on an English screen, because the
 * refusal is the API's sentence and not ours to rewrite.
 */
export function describeError(error: unknown, t: Translate): string {
  if (error instanceof ApiUnreachableError) {
    return t('error.unreachable');
  }
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

export function ErrorMessage({ error, title }: ErrorMessageProps): ReactNode {
  const t = useT();
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const code = error instanceof ApiError ? error.code : undefined;

  return (
    <Alert color="red" title={title ?? t('error.generic')} variant="light">
      <Stack gap="xs">
        <Text size="sm">{describeError(error, t)}</Text>

        {/*
          The request id is how a person gets an answer about their own failure:
          it is the key the API logs are indexed by. Hiding it to keep the alert
          tidy costs them the only thing that makes the log searchable.
        */}
        {requestId !== undefined && (
          <Text size="xs" c="dimmed">
            {t('error.reference')} <Code>{requestId}</Code>
            {code !== undefined && (
              <>
                {' · '}
                <Code>{code}</Code>
              </>
            )}
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
